import { create } from 'zustand';

// Import constants
import {
  INITIAL_END_HOURS,
  INITIAL_START_HOURS,
  MAX_PARTICIPANTS,
} from '../constants/schedule';

// Import types
import type { EventByUser, EventBlock, EraseHover } from '../types/schedule';
import type { DateRange, SaveRange } from '../types/date';
export type HourMode = '12h' | '24h' | string;

type ParticipantsSlice = {
  users: string[];
  currentUser: string;
  joinName: string;
  userIdToName: Record<string, string>;
  ownerByUserId: Record<string, string>;
  eventsByUser: EventByUser;
  setUsers: (users: string[]) => void;
  setCurrentUser: (uid: string) => void;
  setJoinName: (name: string) => void;
  setUserIdToName: (map: Record<string, string>) => void;
  setOwnerByUserId: (map: Record<string, string>) => void;
  setEventsByUser: (e: EventByUser) => void;
};

type EventModalSlice = {
  eventTitle: string;
  eventTimezone: string;
  pickerRange: DateRange;
  pickerStart: string; // HH:MM
  pickerEnd: string; // HH:MM
  setEventTitle: (v: string) => void;
  setEventTimezone: (v: string) => void;
  setPickerRange: (r: DateRange) => void;
  setPickerStart: (v: string) => void;
  setPickerEnd: (v: string) => void;
};

type TimezoneSlice = {
  timezone: string;
  hourMode: HourMode;
  setTimezone: (tz: string) => void;
  setHourMode: (m: HourMode) => void;
  initialTZ: string; // computed at runtime by hook using local timezone; default 'UTC'
  setInitialTZ: (tz: string) => void;
};

type UISlice = {
  title: string;
  setTitle: (v: string) => void;
  isOverlapTimes: boolean;
  setIsOverlapTimes: (v: boolean) => void;
  gridStart: number;
  gridEnd: number;
  setGridStart: (n: number) => void;
  setGridEnd: (n: number) => void;
  hoverCell: { dateKey: string; row: number } | null;
  setHoverCell: (c: { dateKey: string; row: number } | null) => void;
  draftEvent: EventBlock | null;
  setDraftEvent: (e: EventBlock | null) => void;
  eraseHover: EraseHover | null;
  setEraseHover: (e: EraseHover | null) => void;
  hoveredUsers: string[];
  setHoveredUsers: (u: string[]) => void;
};

type EventConfigSlice = {
  savedRange: SaveRange | undefined;
  setSavedRange: (r: SaveRange | undefined) => void;
};

type OwnerSlice = {
  ownerDeviceId: string | undefined;
  ownerTimezone: string | undefined;
  maxParticipants: number;
  setOwnerDeviceId: (id: string | undefined) => void;
  setOwnerTimezone: (tz: string | undefined) => void;
  setMaxParticipants: (max: number) => void;
};

type ModalSlice = {
  isScheduleEventMode: boolean;
  showScheduleModal: boolean;
  showLinkDevicesModal: boolean;
  setIsScheduleEventMode: (v: boolean) => void;
  setShowScheduleModal: (v: boolean) => void;
  setShowLinkDevicesModal: (v: boolean) => void;
};

type ResetSlice = {
  /**
   * Resets all Zustand state fields back to their initial defaults.
   * Methods remain intact because Zustand merges partial state.
   */
  resetRoom: () => void;
};

export type ScheduleStore = ParticipantsSlice &
  EventModalSlice &
  TimezoneSlice &
  UISlice &
  EventConfigSlice &
  OwnerSlice &
  ModalSlice &
  ResetSlice;

// Keep initial defaults centralized
const initialDefaults = {
  // ParticipantsSlice
  users: [] as string[],
  currentUser: '',
  joinName: '',
  userIdToName: {} as Record<string, string>,
  ownerByUserId: {} as Record<string, string>,
  eventsByUser: {} as EventByUser,

  // EventModalSlice
  eventTitle: '',
  eventTimezone: '',
  pickerRange: { from: undefined, to: undefined } as DateRange,
  pickerStart: INITIAL_START_HOURS,
  pickerEnd: INITIAL_END_HOURS,

  // TimezoneSlice
  timezone: 'UTC',
  hourMode: '12h' as HourMode,
  initialTZ: 'UTC',

  // UISlice
  title: '',
  isOverlapTimes: false,
  gridStart: 8,
  gridEnd: 18,
  hoverCell: null as { dateKey: string; row: number } | null,
  draftEvent: null as EventBlock | null,
  eraseHover: null as EraseHover | null,
  hoveredUsers: [] as string[],

  // EventConfigSlice
  savedRange: undefined as SaveRange | undefined,

  // OwnerSlice
  ownerDeviceId: undefined as string | undefined,
  ownerTimezone: undefined as string | undefined,
  maxParticipants: MAX_PARTICIPANTS,

  // ModalSlice
  isScheduleEventMode: false,
  showScheduleModal: false,
  showLinkDevicesModal: false,
};

export const useScheduleStore = create<ScheduleStore>((set) => ({
  // ParticipantsSlice
  users: initialDefaults.users,
  currentUser: initialDefaults.currentUser,
  joinName: initialDefaults.joinName,
  userIdToName: initialDefaults.userIdToName,
  ownerByUserId: initialDefaults.ownerByUserId,
  eventsByUser: initialDefaults.eventsByUser,
  setUsers: (users) => set({ users }),
  setCurrentUser: (uid) => set({ currentUser: uid }),
  setJoinName: (name) => set({ joinName: name }),
  setUserIdToName: (map) => set({ userIdToName: map }),
  setOwnerByUserId: (map) => set({ ownerByUserId: map }),
  setEventsByUser: (e) => set({ eventsByUser: e }),

  // EventModalSlice
  eventTitle: initialDefaults.eventTitle,
  eventTimezone: initialDefaults.eventTimezone,
  pickerRange: initialDefaults.pickerRange,
  pickerStart: initialDefaults.pickerStart,
  pickerEnd: initialDefaults.pickerEnd,
  setEventTitle: (v) => set({ eventTitle: v }),
  setEventTimezone: (v) => set({ eventTimezone: v }),
  setPickerRange: (r) => set({ pickerRange: r }),
  setPickerStart: (v) => set({ pickerStart: v }),
  setPickerEnd: (v) => set({ pickerEnd: v }),

  // TimezoneSlice
  timezone: initialDefaults.timezone,
  hourMode: initialDefaults.hourMode,
  setTimezone: (tz) => set({ timezone: tz }),
  setHourMode: (m) => set({ hourMode: m }),
  initialTZ: initialDefaults.initialTZ,
  setInitialTZ: (tz) => set({ initialTZ: tz }),

  // UISlice
  title: initialDefaults.title,
  setTitle: (title) => set({ title: title }),
  isOverlapTimes: initialDefaults.isOverlapTimes,
  setIsOverlapTimes: (v) => set({ isOverlapTimes: v }),
  gridStart: initialDefaults.gridStart,
  gridEnd: initialDefaults.gridEnd,
  setGridStart: (n) => set({ gridStart: n }),
  setGridEnd: (n) => set({ gridEnd: n }),
  hoverCell: initialDefaults.hoverCell,
  setHoverCell: (c) => set({ hoverCell: c }),
  draftEvent: initialDefaults.draftEvent,
  setDraftEvent: (e) => set({ draftEvent: e }),
  eraseHover: initialDefaults.eraseHover,
  setEraseHover: (e) => set({ eraseHover: e }),
  hoveredUsers: initialDefaults.hoveredUsers,
  setHoveredUsers: (u) => set({ hoveredUsers: u }),

  // EventConfigSlice
  savedRange: initialDefaults.savedRange,
  setSavedRange: (r) => set({ savedRange: r }),

  // OwnerSlice
  ownerDeviceId: initialDefaults.ownerDeviceId,
  ownerTimezone: initialDefaults.ownerTimezone,
  maxParticipants: initialDefaults.maxParticipants,
  setOwnerDeviceId: (id) => set({ ownerDeviceId: id }),
  setOwnerTimezone: (tz) => set({ ownerTimezone: tz }),
  setMaxParticipants: (max) => set({ maxParticipants: max }),

  // ModalSlice
  isScheduleEventMode: initialDefaults.isScheduleEventMode,
  showScheduleModal: initialDefaults.showScheduleModal,
  showLinkDevicesModal: initialDefaults.showLinkDevicesModal,
  setIsScheduleEventMode: (v) => set({ isScheduleEventMode: v }),
  setShowScheduleModal: (v) => set({ showScheduleModal: v }),
  setShowLinkDevicesModal: (v) => set({ showLinkDevicesModal: v }),

  // ResetSlice
  resetRoom: () =>
    set(() => ({
      // Only state keys; methods stay intact due to a shallow merge
      users: initialDefaults.users,
      userIdToName: initialDefaults.userIdToName,
      ownerByUserId: initialDefaults.ownerByUserId,
      eventsByUser: initialDefaults.eventsByUser,

      eventTitle: initialDefaults.eventTitle,
      eventTimezone: initialDefaults.eventTimezone,
      pickerRange: initialDefaults.pickerRange,
      pickerStart: initialDefaults.pickerStart,
      pickerEnd: initialDefaults.pickerEnd,

      savedRange: initialDefaults.savedRange,
      ownerDeviceId: initialDefaults.ownerDeviceId,
      ownerTimezone: initialDefaults.ownerTimezone,
      maxParticipants: initialDefaults.maxParticipants,
      isScheduleEventMode: initialDefaults.isScheduleEventMode,
      showScheduleModal: initialDefaults.showScheduleModal,
      showLinkDevicesModal: initialDefaults.showLinkDevicesModal,
    })),
}));

export default useScheduleStore;
